import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { b2cConfig, loginRequest } from "../config/msalConfig";

/**
 * The single async source of the bearer token.
 *
 * WHY THIS EXISTS. Every API client used to read the token from a snapshot that
 * only a wall-clock timer refreshed — `SessionTimeout` ran `acquireTokenSilent`
 * on mount and then every 50 minutes, against a ~60-minute B2C token. That 10
 * minutes of headroom is only real if the timer fires on time, and it does not:
 * Chrome throttles timers in a backgrounded tab and a sleeping machine skips
 * them entirely, while token expiry keeps running on wall-clock. Walk away from
 * an open tab, come back, click anything, and the request went out with a dead
 * token. The resulting 401 is in NON_RETRYABLE_STATUS_CODES and nothing in the
 * RTK layer handled it, so the app stayed bricked until a full page load — a
 * reload worked only because `authReducer` is persist-blacklisted, so booting
 * re-ran the mount-time acquisition. The refresh token was valid the whole
 * time; nobody ever asked it for a new access token.
 *
 * Acquiring per request removes the staleness window rather than narrowing it.
 * MSAL keeps its own cache, and so does this module, so the steady-state cost
 * of a call here is a timestamp comparison — not a network round-trip.
 */

interface CachedToken {
  accessToken: string;
  expiresAt: number;
}

/**
 * Renew this long before the token actually dies. Covers clock skew between the
 * browser and B2C plus the flight time of a request that is about to be sent.
 */
const EXPIRY_BUFFER_MS = 60_000;

let msalInstance: PublicClientApplication | null = null;
let cached: CachedToken | null = null;

/**
 * The acquisition currently in flight, if any. Without this, an idle tab waking
 * up with twelve queries mounting at once would fire twelve concurrent
 * `acquireTokenSilent` calls — and, on an expired session, twelve competing
 * redirects. `forced` is tracked because a caller recovering from a 401 needs
 * MSAL's own cache bypassed, and must not be handed the result of a
 * non-forced acquisition that may return the very token that just 401'd.
 */
let inFlight: { promise: Promise<string | null>; forced: boolean } | null = null;

/** Set once a redirect has been handed to MSAL, so we never queue a second. */
let redirectStarted = false;

type TokenListener = (accessToken: string, idTokenClaims: unknown) => void;
let listener: TokenListener | null = null;

/**
 * Register a callback fired on every successful acquisition. Exists so Redux
 * can be kept in sync WITHOUT this module importing the store — the store
 * imports `baseApi`, which imports this, so importing the store here would
 * close an import cycle.
 */
export function setTokenListener(cb: TokenListener | null): void {
  listener = cb;
}

export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance;
}

export function getMsalInstanceOrNull(): PublicClientApplication | null {
  return msalInstance;
}

/** Drop every cached token. Called on logout and on instance/tenant switch. */
export function clearCachedToken(): void {
  cached = null;
  inFlight = null;
  redirectStarted = false;
}

/**
 * The cached token if it is still comfortably valid, else null. Synchronous —
 * for callers that cannot await (and as the fast path below).
 */
export function peekCachedToken(): string | null {
  if (cached && Date.now() < cached.expiresAt - EXPIRY_BUFFER_MS) {
    return cached.accessToken;
  }
  return null;
}

/** True while MSAL has a redirect round-trip open; starting another corrupts its cache. */
function interactionInProgress(): boolean {
  try {
    return (
      sessionStorage.getItem(`msal.${b2cConfig.clientId}.interaction.status`) ===
      "interaction_in_progress"
    );
  } catch {
    return false;
  }
}

/**
 * Send the user to B2C, preserving where they were so they land back on the
 * same screen rather than the dashboard. Idempotent: only the first caller in a
 * page lifetime actually redirects.
 */
async function redirectToLogin(instance: PublicClientApplication): Promise<void> {
  if (redirectStarted || interactionInProgress()) return;
  redirectStarted = true;

  const returnTo = `${window.location.pathname}${window.location.search}`;
  const shouldPreserve = returnTo !== "/" && !returnTo.startsWith("/auth");
  if (shouldPreserve) {
    try {
      sessionStorage.setItem("redirectAfterLogin", returnTo);
    } catch {
      // Private-mode storage refusal is not a reason to skip the login itself.
    }
  }

  await instance.loginRedirect({
    ...loginRequest,
    ...(shouldPreserve ? { state: returnTo } : {}),
  });
}

async function acquire(forceRefresh: boolean): Promise<string | null> {
  const instance = msalInstance;
  if (!instance) return null;

  const account = instance.getAllAccounts()[0];
  if (!account) return null;

  try {
    const response = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh,
    });

    if (response?.accessToken) {
      cached = {
        accessToken: response.accessToken,
        // A token with no stated expiry is treated as already stale rather than
        // as immortal: the next call re-acquires instead of pinning a token we
        // cannot reason about for the rest of the session.
        expiresAt: response.expiresOn ? response.expiresOn.getTime() : 0,
      };
      listener?.(response.accessToken, response.idTokenClaims);
      return response.accessToken;
    }
    return null;
  } catch (error) {
    // The session itself is gone — only the user can fix this.
    if (error instanceof InteractionRequiredAuthError) {
      cached = null;
      try {
        await redirectToLogin(instance);
      } catch (redirectError) {
        // `loginRedirect` normally never settles — the browser navigates away.
        // When it DOES reject (MSAL mid-interaction, a blocked navigation), the
        // rejection must stop here: this runs inside `prepareHeaders`, so
        // letting it escape turns a recoverable auth failure into a rejected
        // header build on every request in flight.
        console.error("Failed to start login redirect", redirectError);
      }
      return null;
    }

    // Anything else is transient: offline, a DNS blip, B2C briefly unreachable,
    // the network stack still waking with the machine. Bouncing the user to a
    // login page for a dropped packet would be its own bug, and the caller can
    // recover — an unauthenticated request fails visibly and the 401 path
    // retries. Hand back what we have and let the request decide.
    console.warn("Silent token acquisition failed; using last known token", error);
    return cached?.accessToken ?? null;
  }
}

/**
 * Get a usable access token, acquiring one if the cached copy is spent.
 *
 * Returns null rather than throwing when there is no way to produce a token
 * (MSAL not yet initialized, nobody signed in, or a redirect just started and
 * this page is on its way out). Callers should send the request without an
 * Authorization header and let the server's 401 be the single place that
 * decides what an unauthenticated request means.
 */
export async function getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  const forceRefresh = options?.forceRefresh === true;

  if (!forceRefresh) {
    const fresh = peekCachedToken();
    if (fresh) return fresh;
  }

  // Join an equivalent acquisition already running. A forced caller may only
  // join another forced one; joining a non-forced acquisition could hand it
  // back the same expired token that caused the 401 it is recovering from.
  if (inFlight && (!forceRefresh || inFlight.forced)) {
    return inFlight.promise;
  }

  // Deliberately NOT clearing `cached` here. A forced call already skips the
  // cache read above and tells MSAL to bypass its own, so clearing adds
  // nothing — and it actively harms the failure path: a forced refresh that
  // hits a network blip would discard a token that is still usable, leaving
  // every later request to re-acquire from nothing. Only a SUCCESSFUL
  // acquisition, or a confirmed-dead session, replaces what is cached.
  const promise = acquire(forceRefresh).finally(() => {
    if (inFlight?.promise === promise) inFlight = null;
  });
  inFlight = { promise, forced: forceRefresh };
  return promise;
}
